import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, RefreshCw, Loader2, Pin, Ban, Plus, Trash2, Music as MusicIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VibeOverrideModal — DJ-facing modal for reshaping the auto-detected
 * ambience that gates guest requests.
 *
 * Semantics (two-state model — user-chosen):
 *   • PIN a tag → adds it to the ambience (guests requesting matching
 *     songs will pass even if the queue isn't playing that vibe).
 *   • BLOCK a tag → removes it from the ambience (guests requesting
 *     songs whose only match is a blocked tag will be rejected).
 *   • Overrides persist across sessions until the DJ clicks "Clear all".
 *
 * Data model:
 *   {auto, overrides: {pinned, blocked}, effective, mood_vocabulary}
 *   Each of `pinned` / `blocked` has {moods[], genres[], languages[]}.
 */
export const VibeOverrideModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  const fetchVibe = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hosts/vibe`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load vibe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchVibe();
  }, [open, fetchVibe]);

  // Prevent body scroll while modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const toggle = async (tag, tagType, action) => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/hosts/vibe/toggle`,
        { tag, tag_type: tagType, action },
        { withCredentials: true },
      );
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vibe');
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/hosts/vibe/clear`, {}, { withCredentials: true });
      setData(res.data);
      toast.success('All vibe overrides cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear overrides');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        data-testid="vibe-override-modal"
      >
        {/* Backdrop */}
        <button
          type="button"
          className="absolute inset-0 bg-black/70 backdrop-blur-md"
          onClick={onClose}
          aria-label="Close"
          data-testid="vibe-modal-backdrop"
        />

        {/* Panel */}
        <motion.div
          className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-3xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(157,0,255,0.5)]"
          style={{ background: 'linear-gradient(180deg, rgba(20,14,32,0.98) 0%, rgba(8,4,16,0.98) 100%)' }}
          initial={{ y: 20, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 20, scale: 0.96, opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #9D00FF 0%, #00F5FF 100%)',
                  boxShadow: '0 0 24px rgba(157, 0, 255, 0.4)',
                }}
              >
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white text-lg font-bold font-['Outfit']">Vibe Control</h2>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F5FF]/70 font-['JetBrains_Mono']">
                  Reshape the ambience filter
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchVibe}
                disabled={loading}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                title="Refresh auto-detected vibe"
                data-testid="vibe-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                data-testid="vibe-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="overflow-y-auto px-6 py-5 max-h-[calc(85vh-140px)] space-y-6">
            {loading && !data ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#00F5FF] animate-spin" />
              </div>
            ) : data ? (
              <VibeBody data={data} saving={saving} toggle={toggle} />
            ) : null}
          </div>

          {/* Footer */}
          {data && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-white/8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono']">
                {data.auto.track_count > 0
                  ? `Reading last ${data.auto.track_count} track${data.auto.track_count > 1 ? 's' : ''}`
                  : 'No tracks in queue yet'}
              </p>
              <Button
                onClick={clearAll}
                disabled={saving}
                variant="ghost"
                size="sm"
                className="text-xs text-gray-400 hover:text-[#FF0055] hover:bg-[#FF0055]/10"
                data-testid="vibe-clear-all-btn"
              >
                <Trash2 className="w-3 h-3 mr-1.5" /> Clear all
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/* --------------------------------------------------------------------- */
/* Body                                                                  */
/* --------------------------------------------------------------------- */

const VibeBody = ({ data, saving, toggle }) => {
  const { auto, overrides, effective, mood_vocabulary } = data;

  // Compute the state of each mood tag:
  //   'auto'    — auto-detected, not overridden
  //   'pinned'  — DJ pinned it
  //   'blocked' — DJ blocked it
  //   'idle'    — not detected, not overridden (available for pinning)
  const stateOfMood = (m) => {
    if (overrides.pinned.moods.includes(m)) return 'pinned';
    if (overrides.blocked.moods.includes(m)) return 'blocked';
    if (auto.moods.includes(m)) return 'auto';
    return 'idle';
  };

  const stateOfGenre = (g) => {
    const low = g.toLowerCase();
    if (overrides.pinned.genres.some((x) => x.toLowerCase() === low)) return 'pinned';
    if (overrides.blocked.genres.some((x) => x.toLowerCase() === low)) return 'blocked';
    if (auto.genres.some((x) => x.toLowerCase() === low)) return 'auto';
    return 'idle';
  };

  const stateOfLang = (l) => {
    const low = l.toLowerCase();
    if (overrides.pinned.languages.some((x) => x.toLowerCase() === low)) return 'pinned';
    if (overrides.blocked.languages.some((x) => x.toLowerCase() === low)) return 'blocked';
    if (auto.languages.some((x) => x.toLowerCase() === low)) return 'auto';
    return 'idle';
  };

  return (
    <>
      {/* Effective summary strip */}
      <div
        className="rounded-2xl p-4 border"
        style={{
          background: 'linear-gradient(135deg, rgba(0,245,255,0.06) 0%, rgba(157,0,255,0.06) 100%)',
          borderColor: 'rgba(0, 245, 255, 0.2)',
        }}
        data-testid="vibe-effective-summary"
      >
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F5FF]/80 font-['JetBrains_Mono'] mb-2">
          Effective vibe · what guests see
        </p>
        <div className="flex flex-wrap gap-1.5">
          {[...effective.moods, ...effective.genres, ...effective.languages].length === 0 ? (
            <span className="text-xs text-gray-500 italic">No vibe set — all requests will pass.</span>
          ) : (
            <>
              {effective.moods.map((m) => (
                <SummaryChip key={`e-m-${m}`} label={m} color="teal" />
              ))}
              {effective.genres.map((g) => (
                <SummaryChip key={`e-g-${g}`} label={g} color="purple" />
              ))}
              {effective.languages.map((l) => (
                <SummaryChip key={`e-l-${l}`} label={l} color="pink" />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Moods */}
      <Section title="Moods" subtitle="Tap to pin or block. Detected from the last 5 tracks.">
        <div className="flex flex-wrap gap-2" data-testid="vibe-moods-grid">
          {mood_vocabulary.map((m) => (
            <TagPill
              key={`m-${m}`}
              label={m}
              state={stateOfMood(m)}
              disabled={saving}
              testid={`vibe-mood-${m}`}
              onPin={() => toggle(m, 'mood', 'pin')}
              onUnpin={() => toggle(m, 'mood', 'unpin')}
              onBlock={() => toggle(m, 'mood', 'block')}
              onUnblock={() => toggle(m, 'mood', 'unblock')}
            />
          ))}
        </div>
      </Section>

      {/* Genres */}
      <Section title="Genres" subtitle="Only Apple Music genres detected from the queue.">
        {auto.genres.length === 0 && overrides.pinned.genres.length === 0 && overrides.blocked.genres.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No genres detected yet — play a few tracks.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="vibe-genres-grid">
            {[
              ...auto.genres,
              ...overrides.pinned.genres.filter((g) => !auto.genres.some((x) => x.toLowerCase() === g.toLowerCase())),
              ...overrides.blocked.genres.filter(
                (g) => !auto.genres.some((x) => x.toLowerCase() === g.toLowerCase())
                  && !overrides.pinned.genres.some((x) => x.toLowerCase() === g.toLowerCase()),
              ),
            ].map((g) => (
              <TagPill
                key={`g-${g}`}
                label={g}
                state={stateOfGenre(g)}
                disabled={saving}
                testid={`vibe-genre-${g.toLowerCase().replace(/\s+/g, '-')}`}
                onPin={() => toggle(g, 'genre', 'pin')}
                onUnpin={() => toggle(g, 'genre', 'unpin')}
                onBlock={() => toggle(g, 'genre', 'block')}
                onUnblock={() => toggle(g, 'genre', 'unblock')}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Languages */}
      <Section title="Languages" subtitle="Detected from track metadata.">
        {auto.languages.length === 0 && overrides.pinned.languages.length === 0 && overrides.blocked.languages.length === 0 ? (
          <>
            <p className="text-xs text-gray-500 italic mb-2">No languages detected yet.</p>
            <QuickAddLang toggle={toggle} disabled={saving} />
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="vibe-languages-grid">
              {[
                ...auto.languages,
                ...overrides.pinned.languages.filter((l) => !auto.languages.some((x) => x.toLowerCase() === l.toLowerCase())),
                ...overrides.blocked.languages.filter(
                  (l) => !auto.languages.some((x) => x.toLowerCase() === l.toLowerCase())
                    && !overrides.pinned.languages.some((x) => x.toLowerCase() === l.toLowerCase()),
                ),
              ].map((l) => (
                <TagPill
                  key={`l-${l}`}
                  label={l}
                  state={stateOfLang(l)}
                  disabled={saving}
                  testid={`vibe-lang-${l.toLowerCase()}`}
                  onPin={() => toggle(l, 'language', 'pin')}
                  onUnpin={() => toggle(l, 'language', 'unpin')}
                  onBlock={() => toggle(l, 'language', 'block')}
                  onUnblock={() => toggle(l, 'language', 'unblock')}
                />
              ))}
            </div>
            <QuickAddLang toggle={toggle} disabled={saving} />
          </>
        )}
      </Section>

      {/* Last N tracks preview */}
      {auto.tracks && auto.tracks.length > 0 && (
        <Section title="Reading from" subtitle={`Last ${auto.tracks.length} track${auto.tracks.length > 1 ? 's' : ''} in the queue`}>
          <div className="space-y-1.5" data-testid="vibe-source-tracks">
            {auto.tracks.map((t, i) => (
              <div
                key={`src-${i}`}
                className="flex items-center gap-3 rounded-lg px-3 py-2 bg-white/[0.03] border border-white/5"
              >
                <MusicIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-medium truncate">{t.name}</p>
                  <p className="text-gray-500 text-[10px] truncate">
                    {t.artist} · {[...(t.moods || []), ...(t.genres || []).slice(0, 1)].join(' · ') || 'no tags'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
};


/* --------------------------------------------------------------------- */
/* Bits                                                                  */
/* --------------------------------------------------------------------- */

const Section = ({ title, subtitle, children }) => (
  <div>
    <div className="mb-3">
      <h3 className="text-white text-sm font-bold font-['Outfit']">{title}</h3>
      {subtitle && <p className="text-gray-500 text-[11px]">{subtitle}</p>}
    </div>
    {children}
  </div>
);


const SummaryChip = ({ label, color }) => {
  const colors = {
    teal: 'rgba(0,245,255,0.15) rgba(0,245,255,0.4) #7ff1ff',
    purple: 'rgba(157,0,255,0.15) rgba(157,0,255,0.4) #d1a3ff',
    pink: 'rgba(255,0,127,0.15) rgba(255,0,127,0.4) #ff9fc7',
  };
  const [bg, border, text] = colors[color].split(' ');
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full lowercase"
      style={{ background: bg, border: `1px solid ${border}`, color: text }}
    >
      {label}
    </span>
  );
};


/**
 * TagPill — the interactive tag. Three visual states:
 *   'auto'    — subtle white (detected but no override). One click → BLOCK.
 *   'pinned'  — glowing teal. One click → UNPIN.
 *   'blocked' — muted red, strikethrough. One click → UNBLOCK.
 *   'idle'    — dim outline (not detected). One click → PIN.
 *
 * Each pill has a hover "+ pin" or "× block" hint depending on current
 * state so the DJ knows what the next click will do.
 */
const TagPill = ({
  label, state, disabled, testid,
  onPin, onUnpin, onBlock, onUnblock,
}) => {
  const styles = {
    auto: {
      bg: 'rgba(255,255,255,0.06)',
      border: 'rgba(255,255,255,0.15)',
      color: '#e5e5e5',
      textDecoration: 'none',
      shadow: 'none',
    },
    pinned: {
      bg: 'rgba(0,245,255,0.12)',
      border: 'rgba(0,245,255,0.55)',
      color: '#7ff1ff',
      textDecoration: 'none',
      shadow: '0 0 16px rgba(0,245,255,0.25)',
    },
    blocked: {
      bg: 'rgba(255,0,85,0.08)',
      border: 'rgba(255,0,85,0.4)',
      color: '#ff7a9b',
      textDecoration: 'line-through',
      shadow: 'none',
    },
    idle: {
      bg: 'rgba(255,255,255,0.02)',
      border: 'rgba(255,255,255,0.08)',
      color: '#666',
      textDecoration: 'none',
      shadow: 'none',
    },
  };
  const s = styles[state];

  // Primary action on tap.
  const primary = () => {
    if (disabled) return;
    if (state === 'pinned') onUnpin();
    else if (state === 'blocked') onUnblock();
    else if (state === 'auto') onBlock();
    else onPin(); // idle
  };

  // Secondary action = the opposite (pin vs block). Right-click / long-press.
  const secondary = (e) => {
    if (e) e.preventDefault();
    if (disabled) return;
    if (state === 'pinned') onBlock();
    else if (state === 'blocked') onPin();
    else if (state === 'auto') onPin();
    else onBlock(); // idle → block (rare but supported)
  };

  const icon = state === 'pinned'
    ? <Pin className="w-2.5 h-2.5 fill-current" />
    : state === 'blocked'
      ? <Ban className="w-2.5 h-2.5" />
      : state === 'auto'
        ? <Sparkles className="w-2.5 h-2.5" />
        : <Plus className="w-2.5 h-2.5" />;

  return (
    <button
      type="button"
      onClick={primary}
      onContextMenu={secondary}
      disabled={disabled}
      className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-50 lowercase"
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        textDecoration: s.textDecoration,
        boxShadow: s.shadow,
      }}
      title={
        state === 'pinned' ? 'Pinned — click to unpin, right-click to block'
        : state === 'blocked' ? 'Blocked — click to unblock, right-click to pin'
        : state === 'auto' ? 'Auto-detected — click to block, right-click to pin'
        : 'Not detected — click to pin, right-click to block'
      }
      data-testid={testid}
      data-state={state}
    >
      {icon}
      {label}
    </button>
  );
};


const QuickAddLang = ({ toggle, disabled }) => {
  const [val, setVal] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const v = val.trim().toLowerCase();
    if (!v) return;
    toggle(v, 'language', 'pin');
    setVal('');
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Pin a language (e.g. spanish)"
        disabled={disabled}
        className="flex-1 h-8 rounded-lg bg-white/[0.03] border border-white/10 px-3 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#00F5FF]/40"
        data-testid="vibe-lang-input"
      />
      <button
        type="submit"
        disabled={disabled || !val.trim()}
        className="h-8 px-3 rounded-lg bg-[#00F5FF]/10 border border-[#00F5FF]/30 text-[#7ff1ff] text-xs font-semibold hover:bg-[#00F5FF]/20 disabled:opacity-40 transition-colors"
        data-testid="vibe-lang-add-btn"
      >
        Pin
      </button>
    </form>
  );
};


export default VibeOverrideModal;
